// THE CHANGED-SINCE-RUN DIFF (owner vrequest 2026-09-15, "the change highlight mechanism").
//
// Every ORION in the platform marks a Setup field YELLOW when its live value no longer matches
// what the results on screen were produced with. Maven now does the same, on every level: the
// field, the Calls / Puts side it sits in, the section header, and the collapsed cradle's chip.
//
// PURE FUNCTIONS, no Angular. Two of them:
//   cloneMavenSetup(setup)         — the SNAPSHOT taken at Apply. The live Setup is mutated in
//                                    place by the side components and replaced piecemeal by the
//                                    others, so the baseline needs its own copy of everything.
//   diffMavenSetup(baseline, live) — the live Setup against that snapshot, as a flat map of
//                                    CHANGED-FIELD KEYS (value always `true`). No baseline (never
//                                    run, Reset, portfolio changed) = EMPTY map = every cue off.
//
// KEY GRAMMAR (each key is a plain string):
//   portfolio / template                        — the two pickers
//   date.kind / date.value / date.start / date.end — the questionnaire's Single | Range toggle,
//   exclusions.weekdays / exclusions.dates           the single date, each range boundary on its
//                                                    own, the two exclusion sets
//   bias.enabled / bias.direction               — Directional Bias (inert, highlighted anyway —
//                                                 owner ruling 2026-09-15, "for consistency")
//   existingHedges                              — Ignore | Close
//   existingHedges.expirations / .hedges        — the Close selection (CR-20260916-003), as sets
//   existingHedges.hedge.<groupId>              — ONE hedge whose membership differs (added OR
//                                                 removed since the run) — owner vrequest
//                                                 2026-09-16: the yellow border around its name
//   <side>.enabled                              — the side's checkbox
//   <side>.coverage.kind / .rangeMode / .endStrike / .rangeSize
//   <side>.offset
//   <side>.minProfit.mode / <side>.minProfit.amount
//   <side>.maxRisk.kind / .maxRisk.level / .maxRisk.mode / .maxRisk.amount
//   ovr.<dateKey>.ivCall / .ivPut / .hsCall / .hsPut / .vsCall / .vsPut   — the override boxes, per side
// where <side> is 'calls' or 'puts'.
//
// ⚠ THE `date.*`, `exclusions.*` AND `bias.*` KEYS ARE NOT MAVEN'S TO CHOOSE. The Date Range
// section hosts the APG ORION's own questionnaire (`ets-apg-roll-positions-panel`), and that
// component paints its cues from a `changedKeys` input in ITS key grammar. Maven feeds the map
// straight into it, so these keys are the questionnaire's order form, spelled exactly as it reads
// them. Change them here and the date cues silently stop.
//
// UNION SEMANTICS (the family's ruling shape, CR-20260831-004): when a discriminated union's KIND
// differs, ONLY the kind key is flagged — the newly selected toggle paints yellow — and the inner
// values are NOT compared, because they have no baseline of the same shape. Applies to the date
// kind, the coverage kind, the max-risk kind, and to a side's `enabled` (an unchecked side's
// values are neither shown nor sent). Leaf values are compared only while the kinds match.
// MONEY IS COMPARED AS MONEY (owner vrequest 2026-09-15, second smoke — a DELIBERATE DIVERGENCE
// from the ORION (OPG), which flags mode and amount independently): a Normalized <-> Raw flip
// converts the amount in place, the wire sends plain dollars either way (maven-run-client.ts,
// toPlainDollars), so "10 normalized" and "$10,000 raw" are the same order. Min profit and the
// dollar max risk are therefore compared in PLAIN DOLLARS through the current default quantity:
// the same money flags NOTHING, not even the mode toggle; different money flags the AMOUNT box
// only. With no usable default quantity nothing can be converted, so the raw numbers are compared
// and the mode is judged on its own — the only honest reading left.
//
// EMPTY DISCIPLINE: null and NaN both mean "empty box" and are EQUAL to each other, so clearing a
// box back to the empty state it was sent in does not flag.
//
// ES5 note: arrays + indexOf only (project rule).

import {RollPositionsInputs} from '../../adjustment-pricing-grid/copilot/apg-copilot-domain';
import {copyOverride, MarketConditionOverride, MarketConditionOverrides, OVERRIDE_VALUE_KEYS} from '../../copilot-shared/opg-copilot-domain';
import {mavenRangeMode, MavenSetupInputs, MavenSideInputs} from '../maven-domain';

export type MavenParamChangeMap = { [key: string]: boolean };

// The template guard behind every section header and cradle chip: is ANY key under one of these
// prefixes flagged? A prefix ending in '.' matches a subtree; a bare key matches itself.
export function anyKeyWithPrefix(map: MavenParamChangeMap | null, prefixes: string[]): boolean {
    if (!map) {
        return false;
    }
    for (const k in map) {
        if (!map.hasOwnProperty(k) || !map[k]) {
            continue;
        }
        for (let i = 0; i < prefixes.length; i++) {
            if (k.indexOf(prefixes[i]) === 0) {
                return true;
            }
        }
    }
    return false;
}

// ---- the diff -----------------------------------------------------------------------------

// `defaultQty` is the portfolio's default order quantity as the Setup holds it NOW — the base the
// money conversion runs on (see the header). Null / non-positive = no conversion possible.
export function diffMavenSetup(baseline: MavenSetupInputs | null,
                               live: MavenSetupInputs | null,
                               defaultQty: number | null): MavenParamChangeMap {
    const out: MavenParamChangeMap = {};
    if (!baseline || !live) {
        return out;
    }

    if ((baseline.portfolioId || null) !== (live.portfolioId || null)) {
        out['portfolio'] = true;
    }
    if ((baseline.templateId || null) !== (live.templateId || null)) {
        out['template'] = true;
    }

    diffDates(baseline.dates, live.dates, out);

    if (baseline.existingHedges !== live.existingHedges) {
        out['existingHedges'] = true;
    }

    // CR-20260916-003: the Close selection, compared as SETS (toggling a hedge off and back on
    // is not a change). Only meaningful while both sides are Close; under Ignore both lists are
    // empty and nothing flags. The keys sit under the 'existingHedges' prefix so the section
    // header and the plain Close chip paint from them; the per-side chips watch the hedges key.
    if (live.existingHedges === 'close' && baseline.existingHedges === 'close') {
        const baseSel = baseline.closeSelection || {expirations: [], hedgeIds: []};
        const liveSel = live.closeSelection || {expirations: [], hedgeIds: []};
        if (!sameStringSet(baseSel.expirations, liveSel.expirations)) {
            out['existingHedges.expirations'] = true;
        }
        if (!sameStringSet(baseSel.hedgeIds, liveSel.hedgeIds)) {
            out['existingHedges.hedges'] = true;
            // WHICH hedges (owner vrequest 2026-09-16): the symmetric difference, one key per
            // hedge, so the list can paint the added and the removed rows and no other. An
            // untick-and-retick is in both sets and gets no key.
            symmetricDifference(baseSel.hedgeIds, liveSel.hedgeIds).forEach(id => {
                out['existingHedges.hedge.' + id] = true;
            });
        }
    }

    diffSide(baseline.calls, live.calls, 'calls.', defaultQty, out);
    diffSide(baseline.puts, live.puts, 'puts.', defaultQty, out);

    diffOverrides(baseline.marketOverrides, live.marketOverrides, out);

    return out;
}

// The questionnaire's own grammar (see the header). Its weekday-override rule is carried too: a
// brought-back day is a change to the weekday exclusion the trader is watching, so it paints the
// weekday group — no key of its own.
function diffDates(base: RollPositionsInputs | null, live: RollPositionsInputs | null,
                   out: MavenParamChangeMap): void {
    if (!base || !live) {
        return;
    }
    const bd = base.date;
    const ld = live.date;
    if (bd && ld) {
        if (bd.kind !== ld.kind) {
            out['date.kind'] = true;
        } else if (bd.kind === 'single' && ld.kind === 'single') {
            if (!sameDate(bd.single, ld.single)) {
                out['date.value'] = true;
            }
        } else if (bd.kind === 'range' && ld.kind === 'range') {
            // Each boundary on its own (owner vrequest 2026-09-15): only the box that moved paints.
            if (!sameDate(bd.start, ld.start)) {
                out['date.start'] = true;
            }
            if (!sameDate(bd.end, ld.end)) {
                out['date.end'] = true;
            }
        }
    }

    if (!sameStringSet(base.weekdayExclusions, live.weekdayExclusions)
        || !sameDateSet(base.weekdayOverrides, live.weekdayOverrides)) {
        out['exclusions.weekdays'] = true;
    }
    if (!sameDateSet(base.specificDateExclusions, live.specificDateExclusions)) {
        out['exclusions.dates'] = true;
    }

    const bb = base.bias;
    const lb = live.bias;
    if (bb && lb) {
        if (!!bb.enabled !== !!lb.enabled) {
            out['bias.enabled'] = true;
        } else if (bb.enabled && lb.enabled && bb.direction !== lb.direction) {
            out['bias.direction'] = true;
        }
    }
}

function diffSide(base: MavenSideInputs | null, live: MavenSideInputs | null, prefix: string,
                  defaultQty: number | null, out: MavenParamChangeMap): void {
    if (!base || !live) {
        return;
    }
    if (!!base.enabled !== !!live.enabled) {
        out[prefix + 'enabled'] = true;
        return;
    }
    if (!base.enabled) {
        return;   // both unchecked: nothing shown, nothing sent, nothing to compare
    }

    if ((base.mode || 'custom') !== (live.mode || 'custom')) {
        out[prefix + 'mode'] = true;
        return;
    }
    if (live.mode === 'preset') {
        if (base.presetId !== live.presetId) { out[prefix + 'presetId'] = true; }
        return;
    }
    const bc = base.coverage;
    const lc = live.coverage;
    if (bc && lc) {
        if (bc.kind !== lc.kind) {
            out[prefix + 'coverage.kind'] = true;
        } else if (bc.kind === 'range') {
            if (mavenRangeMode(bc) !== mavenRangeMode(lc)) {
                out[prefix + 'coverage.rangeMode'] = true;
            } else if (mavenRangeMode(lc) === 'distance') {
                if (!numEq(bc.rangeSize, lc.rangeSize)) {
                    out[prefix + 'coverage.rangeSize'] = true;
                }
            } else if (!numEq(bc.endStrike, lc.endStrike)) {
                out[prefix + 'coverage.endStrike'] = true;
            }
        }
    }

    if (!numEq(base.offsetFromShorts, live.offsetFromShorts)) {
        out[prefix + 'offset'] = true;
    }

    const bm = base.minProfit;
    const lm = live.minProfit;
    if (bm && lm) {
        diffMoney(bm.amount, !!bm.normalized, lm.amount, !!lm.normalized, defaultQty, prefix + 'minProfit.', out);
    }

    const br = base.maxRisk;
    const lr = live.maxRisk;
    if (br && lr) {
        if (br.kind !== lr.kind) {
            out[prefix + 'maxRisk.kind'] = true;
        } else if (br.kind === 'dollar' && lr.kind === 'dollar') {
            diffMoney(br.amount, !!br.normalized, lr.amount, !!lr.normalized, defaultQty, prefix + 'maxRisk.', out);
        } else if (br.kind === 'relative' && lr.kind === 'relative') {
            if ((br.level || null) !== (lr.level || null)) {
                out[prefix + 'maxRisk.level'] = true;
            }
        }
    }
}

// A money entry (min profit, dollar max risk) compared AS MONEY — see the header. `prefix` ends in
// '.', and the two keys under it are `mode` and `amount`.
function diffMoney(baseAmount: number | null, baseNormalized: boolean,
                   liveAmount: number | null, liveNormalized: boolean,
                   defaultQty: number | null, prefix: string, out: MavenParamChangeMap): void {
    const convertible = typeof defaultQty === 'number' && isFinite(defaultQty) && defaultQty > 0;
    if (convertible) {
        // Same order, whichever way it is written: nothing. A different order: the amount box.
        if (!numEq(toPlainDollars(baseAmount, baseNormalized, defaultQty as number),
                   toPlainDollars(liveAmount, liveNormalized, defaultQty as number))) {
            out[prefix + 'amount'] = true;
        }
        return;
    }
    // No base to convert on: the raw numbers and the mode are all there is to compare.
    if (baseNormalized !== liveNormalized) {
        out[prefix + 'mode'] = true;
    }
    if (!numEq(baseAmount, liveAmount)) {
        out[prefix + 'amount'] = true;
    }
}

// The wire's own rule (maven-run-client.ts, toPlainDollars): raw = normalized x defaultQty x 100.
// Hand-copied rather than imported — the run client's helper is private, and the two must not
// drift: if that rule ever changes, this one changes with it.
const MAVEN_CONTRACT_MULTIPLIER = 100;

function toPlainDollars(amount: number | null, normalized: boolean, defaultQty: number): number | null {
    if (amount == null || !isFinite(amount)) {
        return null;
    }
    return normalized ? amount * defaultQty * MAVEN_CONTRACT_MULTIPLIER : amount;
}

// The trader's EDITED records only — that is all the map ever holds (the Setup's invariant), so
// the market readings underneath never take part. A record present on one side and absent on the
// other flags every value the present record supplies.
function diffOverrides(baseline: MarketConditionOverrides | null, live: MarketConditionOverrides | null,
                       out: MavenParamChangeMap): void {
    const keys: string[] = [];
    collectKeys(baseline, keys);
    collectKeys(live, keys);
    for (let i = 0; i < keys.length; i++) {
        const dateKey = keys[i];
        const b = baseline ? baseline[dateKey] : null;
        const l = live ? live[dateKey] : null;
        diffOverrideRecord(b || null, l || null, 'ovr.' + dateKey + '.', out);
    }
}

function collectKeys(map: MarketConditionOverrides | null, into: string[]): void {
    if (!map) {
        return;
    }
    for (const k in map) {
        if (map.hasOwnProperty(k) && into.indexOf(k) < 0) {
            into.push(k);
        }
    }
}

function diffOverrideRecord(base: MarketConditionOverride | null, live: MarketConditionOverride | null,
                            prefix: string, out: MavenParamChangeMap): void {
    if (!base && !live) {
        return;
    }
    // CR-20260921-002: one key per side per value — the fieldset paints its own side's boxes.
    for (let i = 0; i < OVERRIDE_VALUE_KEYS.length; i++) {
        const field = OVERRIDE_VALUE_KEYS[i];
        if (!numEq(base ? base[field] : null, live ? live[field] : null)) {
            out[prefix + field] = true;
        }
    }
}

// ---- the snapshot -------------------------------------------------------------------------

// Taken at Apply. Every array and object is Maven's own copy: the side components mutate the
// live objects in place, and a baseline that aliased them would retro-change what the run is
// recorded as having sent, and never flag anything.
export function cloneMavenSetup(s: MavenSetupInputs): MavenSetupInputs {
    return {
        portfolioId: s.portfolioId,
        portfolioOwnerId: s.portfolioOwnerId,
        templateId: s.templateId,
        dates: cloneDates(s.dates),
        existingHedges: s.existingHedges,
        closeSelection: {
            expirations: (s.closeSelection ? s.closeSelection.expirations : []).slice(),
            hedgeIds: (s.closeSelection ? s.closeSelection.hedgeIds : []).slice()
        },
        calls: cloneSide(s.calls),
        puts: cloneSide(s.puts),
        marketOverrides: cloneOverrides(s.marketOverrides)
    };
}

function cloneDates(d: RollPositionsInputs): RollPositionsInputs {
    if (!d) {
        return d;
    }
    const sel = d.date;
    const date: RollPositionsInputs['date'] = (sel && sel.kind === 'single')
        ? {kind: 'single', single: cloneDate(sel.single)}
        : (sel && sel.kind === 'range')
            ? {kind: 'range', start: cloneDate(sel.start), end: cloneDate(sel.end)}
            : {kind: 'range', start: null, end: null};
    return {
        date: date,
        weekdayExclusions: (d.weekdayExclusions || []).slice(),
        specificDateExclusions: (d.specificDateExclusions || []).map(x => cloneDate(x) as Date),
        weekdayOverrides: (d.weekdayOverrides || []).map(x => cloneDate(x) as Date),
        bias: d.bias ? {enabled: d.bias.enabled, direction: d.bias.direction} : d.bias
    };
}

function cloneSide(s: MavenSideInputs): MavenSideInputs {
    if (!s) {
        return s;
    }
    return {
        enabled: s.enabled,
        mode: s.mode, presetId: s.presetId, presetValid: s.presetValid, presetName: s.presetName,
        offsetFromShorts: s.offsetFromShorts,
        coverage: s.coverage ? {...s.coverage} : s.coverage,
        minProfit: s.minProfit ? {amount: s.minProfit.amount, normalized: s.minProfit.normalized} : s.minProfit,
        maxRisk: cloneMaxRisk(s.maxRisk)
    };
}

function cloneMaxRisk(m: MavenSideInputs['maxRisk']): MavenSideInputs['maxRisk'] {
    if (!m) {
        return m;
    }
    return m.kind === 'dollar'
        ? {kind: 'dollar', amount: m.amount, normalized: m.normalized}
        : {kind: 'relative', level: m.level};
}

function cloneOverrides(map: MarketConditionOverrides): MarketConditionOverrides {
    const out: MarketConditionOverrides = {};
    const src = map || {};
    for (const k in src) {
        if (!src.hasOwnProperty(k) || !src[k]) {
            continue;
        }
        out[k] = copyOverride(src[k]);
    }
    return out;
}

// ---- primitives ---------------------------------------------------------------------------

function cloneDate(d: Date | null | undefined): Date | null {
    return d instanceof Date ? new Date(d.getTime()) : null;
}

// null, undefined and NaN all mean "empty box", and are equal to one another.
function numEq(a: number | null | undefined, b: number | null | undefined): boolean {
    const aEmpty = a == null || (typeof a === 'number' && isNaN(a));
    const bEmpty = b == null || (typeof b === 'number' && isNaN(b));
    if (aEmpty || bEmpty) {
        return aEmpty === bEmpty;
    }
    return a === b;
}

function sameDate(x: Date | null | undefined, y: Date | null | undefined): boolean {
    const xv = x instanceof Date && !isNaN(x.getTime());
    const yv = y instanceof Date && !isNaN(y.getTime());
    if (!xv || !yv) {
        return xv === yv;
    }
    return (x as Date).getTime() === (y as Date).getTime();
}

// Order-insensitive: toggling Mon off and back on is not a change.
function sameStringSet(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
    const al = a || [];
    const bl = b || [];
    if (al.length !== bl.length) {
        return false;
    }
    for (let i = 0; i < al.length; i++) {
        if (bl.indexOf(al[i]) < 0) {
            return false;
        }
    }
    return true;
}

// The ids in exactly one of the two lists, each once.
function symmetricDifference(a: string[] | null | undefined, b: string[] | null | undefined): string[] {
    const al = a || [];
    const bl = b || [];
    const out: string[] = [];
    const take = (from: string[], notIn: string[]) => {
        for (let i = 0; i < from.length; i++) {
            if (notIn.indexOf(from[i]) < 0 && out.indexOf(from[i]) < 0) {
                out.push(from[i]);
            }
        }
    };
    take(al, bl);
    take(bl, al);
    return out;
}

function sameDateSet(a: Date[] | null | undefined, b: Date[] | null | undefined): boolean {
    const at = validTimes(a);
    const bt = validTimes(b);
    if (at.length !== bt.length) {
        return false;
    }
    for (let i = 0; i < at.length; i++) {
        if (bt.indexOf(at[i]) < 0) {
            return false;
        }
    }
    return true;
}

function validTimes(list: Date[] | null | undefined): number[] {
    const out: number[] = [];
    (list || []).forEach(d => {
        if (d instanceof Date && !isNaN(d.getTime())) {
            out.push(d.getTime());
        }
    });
    return out;
}
